import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import { ISupplier } from '../api/model/requests';
import { resolveRouterPath, router } from '../router';
import { styles as sharedStyles } from '../styles/shared-styles';
import '../components/data-table';
import {
  createArrayDataProvider,
  type DataTableColumnDef,
} from '../components/data-table';

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

// Per-shop supplier directory. Owner-only writes; reads available to any
// shop member (for delivery + PO selectors). Soft-delete via IsActive —
// deactivated suppliers stay in storage so historical POs / deliveries
// keep their references.
//
// Sub-phase U.2a — rewritten on top of <app-data-table>. Row click
// navigates to the edit page; per-row action column carries the
// deactivate / reactivate icons (owner only).
@customElement('suppliers-list-page')
export class SuppliersListPage extends Base {
  @state() private _suppliers: ISupplier[] = [];
  @state() private _loading = true;
  @state() private _error: string | null = null;
  @state() private _showInactive = false;
  @state() private _busy = false;
  // Free-text search bound to <app-data-table>.freeTextFilter.
  @state() private _search = '';

  private _initStarted = false;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        padding: 16px;
        max-width: 1200px;
        margin: 0 auto;
        box-sizing: border-box;
        width: 100%;
        gap: 12px;
      }
      header.bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        flex-wrap: wrap;
        flex: none;
      }
      .search-row {
        flex: none;
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .search-row sl-input {
        flex: 1 1 auto;
        min-width: 0;
      }
      .search-row sl-input::part(input) {
        font-family: var(--font-mono);
        font-size: 0.9rem;
        letter-spacing: 0.01em;
      }
      .search-meta {
        font-family: var(--font-mono);
        font-size: 0.72rem;
        letter-spacing: 0.06em;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }
      app-data-table {
        flex: 1 1 auto;
        min-height: 0;
      }
      sl-alert {
        flex: none;
      }
      .actions {
        display: flex;
        gap: 8px;
        align-items: center;
      }
      .row-actions {
        display: flex;
        gap: 2px;
        justify-content: flex-end;
      }
      .empty {
        text-align: center;
        padding: 32px 16px;
        color: var(--sl-color-neutral-500);
      }
    `,
  ];

  firstUpdated() {
    if (this.activeShop) void this._init();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeShop') && this.activeShop && !this._initStarted) {
      void this._init();
    }
  }

  private async _init() {
    if (this._initStarted) return;
    this._initStarted = true;
    await this._load();
  }

  private async _load() {
    if (!this.activeShop) {
      this._error = 'No active shop.';
      this._loading = false;
      return;
    }
    this._loading = true;
    this._error = null;
    const r = await ApiFactory.authenticated().listSuppliers(
      this.activeShop.shopName,
      this._showInactive
    );
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Failed to load suppliers.';
    } else {
      this._suppliers = r.data;
    }
    this._loading = false;
  }

  private _openNew() {
    router.navigate(resolveRouterPath('suppliers/new'));
  }

  private _openEdit(s: ISupplier) {
    router.navigate(resolveRouterPath(`suppliers/${encodeURIComponent(s.id)}`));
  }

  private async _deactivate(s: ISupplier, ev: Event) {
    ev.stopPropagation();
    if (this._busy || !this.activeShop) return;
    if (
      !confirm(
        `Deactivate "${s.name}"? Existing POs and deliveries keep their reference.`
      )
    )
      return;
    this._busy = true;
    const r = await ApiFactory.authenticated().deactivateSupplier(
      this.activeShop.shopName,
      s.id
    );
    this._busy = false;
    if (!r.success) {
      this._error = r.error ?? 'Deactivate failed.';
      return;
    }
    await this._load();
  }

  private async _reactivate(s: ISupplier, ev: Event) {
    ev.stopPropagation();
    if (this._busy || !this.activeShop) return;
    this._busy = true;
    const r = await ApiFactory.authenticated().updateSupplier(
      this.activeShop.shopName,
      s.id,
      { isActive: true }
    );
    this._busy = false;
    if (!r.success) {
      this._error = r.error ?? 'Reactivate failed.';
      return;
    }
    await this._load();
  }

  private _toggleShowInactive() {
    this._showInactive = !this._showInactive;
    void this._load();
  }

  private get _dataProvider() {
    return createArrayDataProvider<ISupplier>(() => this._suppliers, {
      filterFn: (item, text) =>
        item.name.toLowerCase().includes(text) ||
        item.id.toLowerCase().includes(text) ||
        (item.contactName ?? '').toLowerCase().includes(text) ||
        (item.contactEmail ?? '').toLowerCase().includes(text),
    });
  }

  private _columns(isOwner: boolean): DataTableColumnDef[] {
    return [
      {
        title: '',
        fieldName: 'isActive',
        width: '36px',
        align: 'center',
        displayValue: (_t, item: ISupplier) =>
          item.isActive
            ? html`<sl-badge
                variant="success"
                pill
                style="--size: 6px;"
              ></sl-badge>`
            : html`<sl-badge
                variant="neutral"
                pill
                style="--size: 6px;"
              ></sl-badge>`,
      },
      {
        title: 'Name',
        fieldName: 'name',
        sortable: true,
        minWidth: '180px',
        displayValue: (_t, item: ISupplier) => html`
          <div>
            <strong>${item.name}</strong>
            <div style="font-size: 12px; opacity: 0.65;">${item.id}</div>
          </div>
        `,
      },
      {
        title: 'Contact',
        fieldName: 'contactName',
        sortable: true,
        minWidth: '140px',
        displayValue: (_t, item: ISupplier) => item.contactName || '—',
      },
      {
        title: 'Email',
        fieldName: 'contactEmail',
        minWidth: '160px',
        displayValue: (_t, item: ISupplier) => item.contactEmail || '—',
      },
      {
        title: 'Phone',
        fieldName: 'contactPhone',
        width: '140px',
        displayValue: (_t, item: ISupplier) => item.contactPhone || '—',
      },
      {
        title: '',
        fieldName: 'id',
        width: '96px',
        fixed: 'right',
        align: 'right',
        displayValue: (_t, item: ISupplier) => {
          if (!isOwner) return html``;
          return html`
            <div
              class="row-actions"
              @click=${(e: Event) => e.stopPropagation()}
            >
              <sl-icon-button
                name="pencil"
                label="Edit"
                @click=${() => this._openEdit(item)}
              ></sl-icon-button>
              ${
                item.isActive
                  ? html`
                      <sl-icon-button
                        name="slash-circle"
                        label="Deactivate"
                        @click=${(e: Event) => this._deactivate(item, e)}
                      ></sl-icon-button>
                    `
                  : html`
                      <sl-icon-button
                        name="arrow-counterclockwise"
                        label="Reactivate"
                        @click=${(e: Event) => this._reactivate(item, e)}
                      ></sl-icon-button>
                    `
              }
            </div>
          `;
        },
      },
    ];
  }

  render() {
    const isOwner =
      this.activeShop &&
      this.userProfile?.hasRole(this.activeShop.shopName, 'owner');

    return html`
      <header class="bar">
        <h2 style="margin: 0;">Suppliers</h2>
        <div class="actions">
          <sl-button size="small" @click=${this._toggleShowInactive}>
            ${this._showInactive ? 'Hide inactive' : 'Show inactive'}
          </sl-button>
          ${
            isOwner
              ? html`
                  <sl-button
                    size="small"
                    variant="primary"
                    @click=${this._openNew}
                  >
                    <sl-icon slot="prefix" name="plus"></sl-icon>
                    New supplier
                  </sl-button>
                `
              : nothing
          }
        </div>
      </header>

      ${
        this._error
          ? html`<sl-alert
              variant="danger"
              open
              closable
              @sl-after-hide=${() => (this._error = null)}
              >${this._error}</sl-alert
            >`
          : nothing
      }
      ${
        this._loading
          ? html`<p>Loading…</p>`
          : this._suppliers.length === 0
            ? html`
                <div class="empty">
                  <p>No suppliers yet.</p>
                  ${
                    isOwner
                      ? html`<sl-button
                          variant="primary"
                          @click=${this._openNew}
                          >Add your first supplier</sl-button
                        >`
                      : nothing
                  }
                </div>
              `
            : html`
                <div class="search-row">
                  <sl-input
                    placeholder="Search suppliers — name, contact, email…"
                    clearable
                    .value=${this._search}
                    @sl-input=${(e: Event) => {
                      this._search = (e.target as HTMLInputElement).value;
                    }}
                  >
                    <sl-icon slot="prefix" name="search"></sl-icon>
                  </sl-input>
                  ${
                    this._search
                      ? html`<span class="search-meta">FILTERING</span>`
                      : html`<span class="search-meta"
                          >${this._suppliers.length} SUPPLIERS</span
                        >`
                  }
                </div>
                <app-data-table
                  fill-height
                  clickrows
                  .columns=${this._columns(!!isOwner)}
                  .dataProvider=${this._dataProvider}
                  .freeTextFilter=${this._search}
                  .keyevent=${(item: ISupplier) => item.id}
                  tablestyle="standardtable"
                  @rowclick=${(e: CustomEvent<{ item: ISupplier }>) =>
                    this._openEdit(e.detail.item)}
                ></app-data-table>
              `
      }
    `;
  }
}
